/**
 * Extract a human-readable part-code (SKU) from a document line item, across
 * every shape the API returns:
 *   - populated ref:      it.partCode = { code: 'CPC-S-2022', ... }
 *   - readable SKU text:  it.partCodeText = 'CPC-S-2022'
 *   - plain string SKU:   it.partCode = 'CPC-S-2022'
 *   - UNPOPULATED ref:    it.partCode = '69e0…ff70'  (24-hex ObjectId) → NOT a code
 *   - legacy GRN field:   it.partNo = 'Q-002040'
 *
 * The bug this fixes: when the API returns `partCode` as an unpopulated
 * ObjectId STRING, the old inline logic
 *   `typeof it.partCode === 'string' ? it.partCode : (it.partCode?.code ?? it.partCodeText)`
 * took the ObjectId branch, flagged it as "bad", and showed "—" — never
 * falling back to the perfectly good `partCodeText`. So PO lines that DID
 * have a code (e.g. CPC-S-2022) rendered blank.
 *
 * Returns '' when no readable code exists. Never returns an ObjectId.
 */
const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

export function displayPartCode(it: any): string {
  if (!it) return '';
  // 1. Populated ref object — the canonical readable code.
  if (it.partCode && typeof it.partCode === 'object' && it.partCode.code) {
    return String(it.partCode.code).trim();
  }
  // 2. Stored readable SKU text — always present once the line is resolved,
  //    and the reason the ObjectId-string case must fall through to here.
  //    Guard against corrupt rows where the PartCode ObjectId was mistakenly
  //    written INTO partCodeText (the "code shows as an ObjectId" bug) — a
  //    24-hex value is never a real SKU, so skip it.
  const text = String(it.partCodeText ?? '').trim();
  if (text && !OBJECT_ID_RE.test(text)) return text;
  // 3. Plain string SKU (sheet-only lines) — but never an ObjectId.
  if (typeof it.partCode === 'string') {
    const s = it.partCode.trim();
    if (s && !OBJECT_ID_RE.test(s)) return s;
  }
  // 4. Legacy GRN/issue surfaces use `partNo`.
  const partNo = String(it.partNo ?? '').trim();
  if (partNo && !OBJECT_ID_RE.test(partNo)) return partNo;
  return '';
}

/** True when the line has a readable part code to show. */
export function hasPartCode(it: any): boolean {
  return displayPartCode(it).length > 0;
}
